import {InputProps, MenuItem, TextField, TextFieldProps} from '@mui/material';
import _ from 'lodash';
import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import API from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {useAppSelector} from '../../hooks/redux';
import reduxSelectors from '../../redux/selectors';

type Props = Pick<
  TextFieldProps,
  | 'size'
  | 'disabled'
  | 'fullWidth'
  | 'label'
  | 'error'
  | 'helperText'
  | 'InputProps'
  | 'SelectProps'
  | 'sx'
> & {
  value?: number | number[] | null;
  multiple?: boolean;
  nullLabel?: string;
  onChange?: (v: number | number[]) => void;
};

type SitePermission = {
  site_id: number;
  user_id: number;
  allowed: number | boolean;
  id: number;
};

export const SitesSelect = (props: Props) => {
  // This variable is needed only for multiple mode
  const [selected, setSelected] = useState<number[]>(
    props.multiple ? (props.value as number[]) : []
  );

  const me = useAppSelector(({app}) => app.me);
  const assets = useSelector(reduxSelectors.assets.getAssets);
  //const options = assets.sites;
  const allSites = assets.sites;
  const [allowedSites, setAllowedSites] = useState<any[]>([]);

  const fetchAllowedSites = async () => {
    const resp = await API.get(`${apiBaseUrl}/site-permissions/user/${me?.id}`);
    const userSitesPermissions = resp.data || [];
    const _allowedSites = allSites.filter((allSite) => {
      // if a permission exists and allowed is false then remove the site from the array
      const match = userSitesPermissions.find(
        (permission: SitePermission) => permission.site_id === allSite.id
      );
      return !match || match.allowed === 1;
    });
    setAllowedSites(_allowedSites);
    return resp.data;
  };

  const userHasAllAccess = allowedSites.length === allSites.length;

  useEffect(() => {
    fetchAllowedSites();
    if (!selected && !userHasAllAccess && allowedSites.length) {
      setSelected([allowedSites[0]]);
    }
  }, []);

  // if props.multiple, valueName is an array
  const valueName = props.multiple
    ? selected?.length > 0
      ? selected
      : userHasAllAccess
        ? ['all']
        : [allowedSites[0]?.id]
    : props.value
      ? props.value
      : userHasAllAccess
        ? 'all'
        : allowedSites[0]?.id ?? '';

  const handleChange: InputProps['onChange'] = (event: any) => {
    // For single mode we return value immediately
    if (!props.multiple) {
      return props.onChange?.(
        event.target.value === 'all' ? null : event.target.value
      );
    }

    // For multiple mode we need to update array of selected items
    // and return them later when dropdown is closed
    const isSelectAll = _.last(event.target.value) === 'all';
    setSelected(
      isSelectAll ? [] : event.target.value.filter((i: any) => i !== 'all')
    );
  };

  const handleClose = () => {
    // For multiple mode we return value only after dropdown is closed
    if (props.multiple) {
      props.onChange?.(selected);
    }
  };

  return (
    <TextField
      {...props}
      label={props.label || 'Sites'}
      value={valueName}
      select
      SelectProps={{
        multiple: props.multiple,
        onClose: handleClose,
        MenuProps: {
          sx: {maxHeight: '500px'},
        },
      }}
      onChange={handleChange}
    >
      {userHasAllAccess && (
        <MenuItem value="all">{props.nullLabel ?? 'All Sites'}</MenuItem>
      )}

      {allowedSites.map((site) => (
        <MenuItem
          key={site.id}
          value={site.id}
          disabled={site.status === 'inactive'}
        >
          {site.name}
        </MenuItem>
      ))}
    </TextField>
  );
};
